/**
 * FLAGS — route/page guard by feature.
 *
 * `<FeatureGate feature="billing.core">…</FeatureGate>` shows the content
 * only if the feature is effectively-enabled in the context (active org by
 * default, optional course). Disabled ⇒ friendly "disabled by
 * your academy" state (not a 404): it explains what's happening, that DATA IS PRESERVED and
 * links to the help center's guide when it exists.
 *
 * UX ONLY — the authority is the server (`feature_gate` → structured 403).
 */
import { type Accessor, type Component, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { ToggleLeft } from "@fleet/icons";
import { featureGraph } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/solid-router";
import { useFeatureEnabled } from "./use-feature-enabled";

/** Premium "feature disabled by your academy" state. */
const FeatureDisabledState: Component<{
  /** Requested feature's key (for the manifest's readable name). */
  feature: string;
  /** Culprit key (a graph requirement) if it differs from the one requested. */
  blockedBy?: string;
  class?: string;
}> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const graph = featureGraph();
  const label = (key: string) => graph.get(key)?.name ?? key;
  const description = () => {
    const featureName = label(props.feature);
    if (props.blockedBy && props.blockedBy !== props.feature) {
      const blockerName = label(props.blockedBy);
      return t(
        msg`"${featureName}" needs "${blockerName}", which your academy has turned off. Nothing was deleted: everything comes back when it's re-enabled.`,
      );
    }
    return t(
      msg`Your academy has turned off "${featureName}". Nothing was deleted: your data is preserved and everything comes back when it's re-enabled.`,
    );
  };

  return (
    <div data-testid="feature-disabled-state" class={props.class ?? "mx-auto max-w-2xl px-4 py-16"}>
      <EmptyState
        icon={ToggleLeft}
        title={t(msg`Disabled by your academy`)}
        description={description()}
      />
      <div class="mt-4 flex justify-center">
        <Button
          variant="outline"
          data-testid="feature-disabled-home"
          onClick={() => {
            navigate({ to: "/learn" });
          }}
        >
          {t(msg`Go to Learn`)}
        </Button>
      </div>
    </div>
  );
};

/**
 * HOC for routes: `component: withFeatureGate("billing.core", FinancePage)`.
 * The route keeps existing (no 404); its content is replaced by the
 * "disabled by your academy" state when the feature is off.
 */
export function withFeatureGate(feature: string, Comp: Component): Component {
  return () => (
    <FeatureGate feature={feature}>
      <Comp />
    </FeatureGate>
  );
}

/** Wraps the content of a feature-gated page. */
export const FeatureGate: Component<{
  feature: string;
  orgId?: Accessor<string | undefined>;
  courseId?: Accessor<string | undefined>;
  children: JSX.Element;
}> = (props) => {
  const state = useFeatureEnabled(() => props.feature, {
    orgId: () => props.orgId?.(),
    courseId: () => props.courseId?.(),
  });
  return (
    <Show
      when={state().enabled}
      fallback={<FeatureDisabledState feature={props.feature} blockedBy={state().blockedBy} />}
    >
      {props.children}
    </Show>
  );
};
